import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icono } from './Iconos';

// Tema compartido "Contador Nixie". Los colores salen de las variables CSS de
// theme.css; así las pantallas aún no migradas (bloque .legacy-light) reciben
// los mismos componentes en claro sin tocar su código.

// Colores por rol afinados para acero (hex: algunas pantallas les concatenan opacidad)
export const ROL_COLORS   = { admin: '#6d28d9', finanzas: '#0369a1', solicitante: '#15803d', director: '#b45309', contador: '#0f766e', programacion: '#be185d', jefe_compras: '#b45309', comprador: '#4338ca' };
// El texto de cada estado es el mismo tono del punto (--st-*, tonos 700 que dan
// AA sobre blanco); el fondo y el borde son ese mismo tono al 8% y al 30%.
const estado = (v, rgb, label) => ({ bg: `rgba(${rgb},0.08)`, border: `rgba(${rgb},0.30)`, text: `var(${v})`, dot: `var(${v})`, label });
export const ESTADO_META  = {
  pendiente: estado('--st-pendiente', '180,83,9', 'Validación Contable'),
  aprobada:  estado('--st-aprobada', '21,128,61', 'Programación de pago'),
  por_pagar: estado('--st-por-pagar', '3,105,161', 'Por pagar'),
  rechazada: estado('--st-rechazada', '190,18,60', 'Rechazada'),
  pagada:    estado('--st-pagada', '29,78,216', 'Pagada'),
};
export const ROL_LABELS   = { admin: 'Admin', finanzas: 'Finanzas', solicitante: 'Solicitante', director: 'Director', contador: 'Contador', programacion: 'Programación', jefe_compras: 'Jefe de Compras', comprador: 'Comprador' };
// Hex (no variables) porque los historiales les concatenan opacidad: `${c}18`.
// Afinados para leerse sobre acero; sus únicos consumidores ya están migrados.
export const ESTADO_COLORS = { pendiente: '#b45309', aprobada: '#15803d', por_pagar: '#0369a1', rechazada: '#be123c', pagada: '#1d4ed8' };

export function opcionesEstado(rol, item) {
  const TODOS_ESTADOS = ['pendiente', 'aprobada', 'por_pagar', 'rechazada', 'pagada'];
  if (rol === 'admin') return TODOS_ESTADOS;
  if (rol === 'contador')
    return item.estado === 'pendiente' ? ['pendiente', 'aprobada', 'rechazada'] : null;
  if (rol === 'programacion') {
    const operable = item.flujo !== 'v2'
      ? item.estado !== 'pagada'
      : ['aprobada', 'por_pagar'].includes(item.estado);
    return operable ? [...new Set([item.estado, 'por_pagar', 'pagada', 'rechazada'])] : null;
  }
  return null;
}
export const ESTADO_OPCION_LABEL = { pendiente: 'Validación Contable', por_pagar: 'por pagar', aprobada: 'programación de pago', rechazada: 'Rechazada', pagada: 'Pagada' };
export const ESTADO_LABEL = { pendiente: 'Validación Contable', aprobada: 'Programación de pago', por_pagar: 'Por pagar', rechazada: 'Rechazada', pagada: 'Pagada' };

// Solo hay tres botones (4 nov 2026): primario naranja, secundario gris y
// peligro. Los colores que las pantallas todavía pasan a css.btn() se traducen
// aquí: el naranja, el índigo del tema anterior y los de "confirmar" (turquesa
// de programar, azul de pagar) son el primario; el rojo es peligro; todo lo
// demás (verde de exportar, azul de importar o buscar, grises) es secundario.
const PRIMARIOS = ['#5e72e4', '#ff541c', '#f47920', '#00b8c4', '#3b82f6', '#2563eb', '#9d4edd'];
const PELIGRO = ['#ef4444', '#dc2626', '#be123c', '#b91c1c'];
const variante = (color) => {
  const c = String(color || '').toLowerCase();
  if (PRIMARIOS.includes(c) || c === 'primario') return 'primario';
  if (PELIGRO.includes(c) || c === 'peligro') return 'peligro';
  return 'secundario';
};

const PLACA = {
  padding: '8px 18px', borderRadius: 4,
  fontFamily: 'var(--btn-font)', fontSize: 'var(--btn-size)', fontWeight: 600,
  letterSpacing: 'var(--btn-track)', textTransform: 'var(--btn-case)',
  cursor: 'pointer', lineHeight: 1.2,
};

export const css = {
  input: (err) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', borderRadius: 4,
    border: `1px solid ${err ? 'var(--danger)' : 'var(--mesh)'}`,
    fontSize: 13.5, fontFamily: 'Inter, sans-serif',
    background: err ? 'var(--danger-bg)' : 'var(--well)',
    color: 'var(--ink)', outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  }),
  inputRO: { background: 'var(--panel-hi)', color: 'var(--glass)', cursor: 'default' },
  select: (err) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '9px 32px 9px 12px', borderRadius: 4,
    border: `1px solid ${err ? 'var(--danger)' : 'var(--mesh)'}`,
    fontSize: 13.5, fontFamily: 'Inter, sans-serif',
    background: err ? 'var(--danger-bg)' : 'var(--well)',
    color: 'var(--ink)', outline: 'none',
    appearance: 'none', cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24'%3E%3Cpath fill='%238a9098' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  }),
  card: {
    boxShadow: 'var(--card-shadow)',
    background: 'var(--screws), var(--panel)',
    borderRadius: 6,
    border: '1px solid var(--mesh)',
  },
  // Botón ghost: placa gris
  btnGhost: {
    ...PLACA,
    background: 'var(--ghost-bg)', border: '1px solid var(--ghost-line)', color: 'var(--ghost-fg)',
  },
  // css.btn('primario' | 'secundario' | 'peligro', contorno?) — acepta también
  // los colores antiguos (ver `variante`)
  btn: (color = 'primario', outlined = false) => {
    const v = variante(color);
    if (v === 'primario') {
      return outlined
        ? { ...PLACA, background: 'transparent', border: '1px solid var(--plasma-line)', color: 'var(--plasma)' }
        : { ...PLACA, background: 'var(--btn-bg)', border: '1px solid var(--btn-line)', color: 'var(--btn-fg)', boxShadow: 'var(--btn-shadow)' };
    }
    if (v === 'peligro') {
      return outlined
        ? { ...PLACA, background: 'var(--ghost-bg)', border: '1px solid var(--danger-line)', color: 'var(--danger)' }
        : { ...PLACA, background: 'var(--danger)', border: '1px solid var(--danger)', color: '#ffffff' };
    }
    return { ...PLACA, background: 'var(--ghost-bg)', border: '1px solid var(--ghost-line)', color: 'var(--ghost-fg)' };
  },
};

export const Label = ({ children, required }) => (
  <label className="nx-label" style={{ display: 'block', marginBottom: 6 }}>
    {children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
  </label>
);

// Destino del pago (a quién y a qué cuenta) en el formulario de "corregir y
// reenviar": se muestra pero NO se edita. Cambiarlo mandaría el dinero a otro
// lado con un folio que el aprobador ya había visto, así que el servidor lo
// rechaza (backend/src/utils/datosBancarios.js) y aquí ni siquiera se ofrece.
// `campos` es una lista de [etiqueta, valor].
export const DestinoPagoFijo = ({ campos = [] }) => (
  <div style={{ borderRadius: 4, border: '1px solid var(--mesh)', background: 'var(--well)', padding: '10px 12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--plasma)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      <span className="nx-label">Destino del pago (no se puede cambiar)</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 12px' }}>
      {campos.filter(([, v]) => v).map(([etiqueta, valor]) => (
        <div key={etiqueta} style={{ fontSize: 12.5, minWidth: 0 }}>
          <span style={{ color: 'var(--glass)' }}>{etiqueta}: </span>
          <span title={String(valor)} style={{ color: 'var(--ink)', fontWeight: 600, fontFamily: /CLABE|Cuenta/i.test(etiqueta) ? 'var(--digit-font)' : 'inherit', wordBreak: 'break-word' }}>{valor}</span>
        </div>
      ))}
    </div>
    <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 8 }}>
      Para pagar a otra cuenta o a otro beneficiario, corrige el catálogo y crea una solicitud nueva: así vuelve a pasar por el flujo de aprobación completo.
    </div>
  </div>
);

export const Grid = ({ cols = 1, gap = 14, children, style }) => (
  <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap, ...style }}>
    {children}
  </div>
);

export function formatMiles(raw) {
  if (raw === '' || raw == null) return '';
  let s = String(raw);
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  const dot = s.indexOf('.');
  let ent = dot >= 0 ? s.slice(0, dot) : s;
  const dec = dot >= 0 ? s.slice(dot + 1) : null;
  ent = ent.replace(/^0+(?=\d)/, '');
  if (ent === '') ent = '0';
  ent = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + ent + (dec !== null ? '.' + dec : '');
}

export function limpiarNumero(text) {
  let s = String(text).replace(/[^\d.]/g, '');
  const i = s.indexOf('.');
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
}

export function MontoInput({ value, onChange, err, style, placeholder = '0.00' }) {
  return (
    <input
      type="text" inputMode="decimal"
      value={formatMiles(value)}
      onChange={e => onChange(limpiarNumero(e.target.value))}
      placeholder={placeholder}
      style={{ ...css.input(err), fontFamily: 'var(--digit-font)', ...style }}
    />
  );
}

export const SectionHeader = ({ children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '22px 0 12px', paddingBottom: 8,
    borderBottom: '1px solid var(--mesh-soft)',
  }}>
    <span className="nx-label" style={{ color: 'var(--plasma)' }}>{children}</span>
  </div>
);

export const Badge = ({ children, color = 'var(--glass)', bg }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: 3,
    fontSize: 11.5, fontWeight: 600,
    color, background: bg || 'var(--panel-hi)',
    border: '1px solid var(--mesh-soft)',
  }}>{children}</span>
);

// `accion`: { label, onClick } — p. ej. "Deshacer" tras cambiar un estado
export const Toast = ({ msg, tipo, onClose, accion, abajo }) => msg ? (
  <div role="status" onClick={onClose} style={{
    position: 'fixed', zIndex: 9999, cursor: 'pointer',
    ...(abajo ? { bottom: 24, left: '50%', transform: 'translateX(-50%)' } : { top: 20, right: 20 }),
    background: 'var(--panel)',
    border: `1px solid ${tipo === 'error' ? 'var(--danger-line)' : 'rgba(21,128,61,0.45)'}`,
    color: 'var(--ink)',
    padding: '12px 16px 12px 14px', borderRadius: 4,
    fontSize: 13, fontWeight: 500,
    boxShadow: '0 10px 30px rgba(17,17,17,0.14)',
    maxWidth: 440, minWidth: 240,
    display: 'flex', alignItems: 'center', gap: 10,
    animation: abajo ? 'none' : 'slideInRight 0.2s ease',
  }}>
    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: tipo === 'error' ? 'var(--danger)' : 'var(--ok)', }} />
    <span style={{ flex: 1 }}>{msg}</span>
    {accion && (
      <button type="button" onClick={e => { e.stopPropagation(); accion.onClick(); }}
        style={{ ...PLACA, padding: '5px 12px', fontSize: 13, background: 'var(--ghost-bg)', border: '1px solid var(--plasma-line)', color: 'var(--plasma)', flexShrink: 0 }}>
        {accion.label}
      </button>
    )}
    <style>{`@keyframes slideInRight { from { opacity:0; transform:translateX(16px); } to { opacity:1; transform:translateX(0); } }`}</style>
  </div>
) : null;

// Pila de modales abiertos: Esc y el atrapado de Tab solo actúan sobre el de
// arriba, para que cerrar un modal encimado no cierre también el de abajo.
const pilaModales = [];
const ENFOCABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, width = 480, zIndex = 1000 }) {
  const caja = useRef(null);
  const cerrar = useRef(onClose);
  cerrar.current = onClose;
  const idTitulo = useId();

  // Teclado: Esc cierra, Tab no se sale del modal, el foco entra al abrir y
  // vuelve al botón que lo abrió al cerrar.
  useEffect(() => {
    if (!open) return undefined;
    const previo = document.activeElement;
    const yo = {};
    pilaModales.push(yo);
    const t = setTimeout(() => {
      const c = caja.current;
      if (!c) return;
      const campo = c.querySelector('.nx-modal-cuerpo input:not([disabled]), .nx-modal-cuerpo select:not([disabled]), .nx-modal-cuerpo textarea:not([disabled])');
      (campo || c).focus();
    }, 0);
    const tecla = (e) => {
      if (pilaModales[pilaModales.length - 1] !== yo) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return; // un control interno (p. ej. un desplegable) ya lo atendió
        e.preventDefault();
        cerrar.current?.();
        return;
      }
      if (e.key !== 'Tab' || !caja.current) return;
      const els = [...caja.current.querySelectorAll(ENFOCABLES)].filter(el => el.offsetParent !== null);
      if (!els.length) { e.preventDefault(); return; }
      const primero = els[0], ultimo = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === primero || document.activeElement === caja.current)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', tecla);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', tecla);
      pilaModales.splice(pilaModales.indexOf(yo), 1);
      if (previo && typeof previo.focus === 'function' && document.contains(previo)) previo.focus();
    };
  }, [open]);

  if (!open) return null;
  // Se dibuja en <body>: dentro del área principal quedaría debajo del menú
  // lateral (el main crea su propia capa de apilamiento).
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(17,17,17,0.45)',
      zIndex, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
      animation: 'modalBgIn 0.15s ease',
    }}>
      <div ref={caja} role="dialog" aria-modal="true" aria-labelledby={idTitulo} tabIndex={-1} onClick={e => e.stopPropagation()} style={{
        outline: 'none',
        background: 'var(--screws), var(--panel)', border: '1px solid var(--mesh)',
        borderRadius: 6, width, maxWidth: '100%',
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(17,17,17,0.18)', color: 'var(--ink)',
        animation: 'modalIn 0.18s ease',
      }}>
        <div style={{
          padding: '14px 20px 14px 24px', borderBottom: '1px solid var(--mesh-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span id={idTitulo} style={{ fontFamily: 'var(--label-font)', fontSize: 17, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink)' }}>{title}</span>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="nx-ghost" style={{
            fontSize: 16, width: 30, height: 30,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><Icono nombre="cerrar" size={15} /></button>
        </div>
        <div className="nx-modal-cuerpo" style={{ padding: '20px 24px' }}>{children}</div>
      </div>
      <style>{`
        @keyframes modalBgIn { from { opacity:0; } to { opacity:1; } }
        @keyframes modalIn   { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  , document.body);
}

export function PageHeader({ icon, title, subtitle, actions }) {
  return (
    <div style={{
      padding: '0 0 14px', marginBottom: 20,
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
      borderBottom: '1px solid var(--mesh-soft)',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div>
        <h1 style={{ margin: 0, fontFamily: 'var(--label-font)', fontSize: 24, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1, letterSpacing: '.05em', textTransform: 'uppercase' }}>{title}</h1>
        {subtitle && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--glass)' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function EstadoBadge({ estado }) {
  const m = ESTADO_META[estado] || { bg: 'var(--panel-hi)', border: 'var(--mesh)', text: 'var(--glass)', dot: 'var(--steel)', label: estado };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 8px', borderRadius: 3, fontSize: 11.5, fontWeight: 600,
      background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: 'nowrap',
    }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

export function IconBtn({ onClick, title, icon, danger, disabled }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} disabled={disabled} className="db-icon-btn"
      style={{ opacity: disabled ? 0.4 : 1, fontSize: 13, ...(danger ? { color: 'var(--danger)', borderColor: 'var(--danger-line)' } : {}) }}>
      {icon}
    </button>
  );
}

export function Paginacion({ page, pages, onPage }) {
  if (!pages || pages <= 1) return null;
  const btn = (label, target, disabled, title) => (
    <button type="button" onClick={() => onPage(target)} disabled={disabled} title={title} aria-label={title}
      className="nx-ghost"
      style={{ padding: '5px 11px', fontSize: 13, fontWeight: 600, opacity: disabled ? 0.35 : 1 }}>
      {label}
    </button>
  );
  return (
    <div style={{ padding: '12px 20px', borderTop: '1px solid var(--mesh-soft)', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {btn('«', 1, page <= 1, 'Primera página')}
      {btn('‹', page - 1, page <= 1, 'Página anterior')}
      <span style={{ fontSize: 12.5, color: 'var(--glass)', padding: '0 8px', fontFamily: 'var(--digit-font)' }}>
        {page} / {pages}
      </span>
      {btn('›', page + 1, page >= pages, 'Página siguiente')}
      {btn('»', pages, page >= pages, 'Última página')}
    </div>
  );
}

export function Spinner() {
  return (
    <div role="status" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, gap:14 }}>
      <div style={{ position:'relative', width:36, height:36 }}>
        <div style={{ position:'absolute', inset:0, border:'2px solid var(--mesh)', borderRadius:'50%' }} />
        <div style={{
          position:'absolute', inset:0,
          border:'2px solid transparent', borderTopColor:'var(--plasma)', borderRadius:'50%',
          animation:'spin 0.7s cubic-bezier(0.4,0,0.2,1) infinite',
        }} />
      </div>
      <span className="nx-label">Cargando…</span>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}

// Cifra en tubos nixie: cada dígito en su tubo encendido, la puntuación sin
// tubo. Firma del tema: cuando el valor cambia, la cifra anterior se apaga
// encima mientras la nueva se enciende debajo (un cross-fade por actualización).
function Cifra({ s, tubos, size }) {
  if (!tubos) return <span className="nx-plain" style={{ fontSize: size }}>{s}</span>;
  return (
    <span className="nx-digits" style={{ fontSize: size }}>
      {s.split('').map((c, i) => /\d/.test(c)
        ? <span key={i} className="nx-tube">{c}</span>
        : <span key={i} className="nx-sep">{c}</span>)}
    </span>
  );
}

// Variante "viva" para lecturas que cambian solas (reloj, barrido de cátodos):
// solo el tubo cuyo dígito cambió se vuelve a encender; los demás siguen quietos.
export function NixieVivo({ value, size = 28, label }) {
  const s = String(value);
  return (
    <span className="nx-digits nx-vivo" style={{ fontSize: size }} role="img" aria-label={label || s}>
      {s.split('').map((c, i) => /\d/.test(c)
        ? <span key={`${i}-${c}`} className="nx-tube" aria-hidden="true">{c}</span>
        : <span key={`${i}-sep`} className="nx-sep" aria-hidden="true">{c}</span>)}
    </span>
  );
}

export function Nixie({ value, size = 28, tubos = true, label, alerta = false }) {
  const s = String(value);
  const previo = useRef(s);
  const [saliente, setSaliente] = useState(null);
  useEffect(() => {
    if (previo.current === s) return undefined;
    setSaliente(previo.current);
    previo.current = s;
    const t = setTimeout(() => setSaliente(null), 600);
    return () => clearTimeout(t);
  }, [s]);
  return (
    <span className={`nx-stack${alerta ? ' nx-alerta' : ''}`} role="img" aria-label={label || s}>
      <span key={s} className={saliente ? 'nx-ignite' : undefined} aria-hidden="true"><Cifra s={s} tubos={tubos} size={size} /></span>
      {saliente && <span className="nx-fade" aria-hidden="true"><Cifra s={saliente} tubos={tubos} size={size} /></span>}
    </span>
  );
}

// Acción con "Deshacer" (4 nov 2026): el cambio no se manda al servidor hasta
// que pasan unos segundos; mientras tanto un aviso abajo deja deshacerlo.
// Se aplica antes si se toca el aviso, si llega otra acción o si la pantalla se
// cierra; al intentar salir del navegador con una pendiente, el navegador pregunta.
//   const { programar, aviso } = useDeshacer();
//   programar('Aprobada ALGT-SS-2026-00001 · $1,000.00', () => aprobar(s));
//   ... {aviso}
export function useDeshacer(ms = 5000) {
  const pendiente = useRef(null);
  const [msg, setMsg] = useState(null);
  const aplicar = useCallback(() => {
    const p = pendiente.current;
    if (!p) return;
    clearTimeout(p.t);
    pendiente.current = null;
    setMsg(null);
    p.fn();
  }, []);
  const deshacer = useCallback(() => {
    const p = pendiente.current;
    if (!p) return;
    clearTimeout(p.t);
    pendiente.current = null;
    setMsg(null);
  }, []);
  const programar = useCallback((texto, fn) => {
    aplicar();
    pendiente.current = { fn, t: setTimeout(aplicar, ms) };
    setMsg(texto);
  }, [aplicar, ms]);
  useEffect(() => {
    const salir = (e) => { if (pendiente.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', salir);
    return () => {
      window.removeEventListener('beforeunload', salir);
      // la pantalla se cierra: lo pendiente se aplica, no se pierde
      const p = pendiente.current;
      if (p) { clearTimeout(p.t); pendiente.current = null; p.fn(); }
    };
  }, []);
  const aviso = msg ? <Toast abajo msg={msg} onClose={aplicar} accion={{ label: 'Deshacer', onClick: deshacer }} /> : null;
  return { programar, aviso };
}

// Estado de una solicitud que el rol puede cambiar (4 nov 2026): se ve como el
// badge de estado con una flecha y abre un menú; ya no es un <select> que
// aprueba con solo moverse con las flechas del teclado. Teclado: Enter, Espacio
// o flecha abajo abren; flechas recorren; Enter elige; Esc cierra.
export function EstadoMenu({ estado, opciones, onElegir, etiquetas = ESTADO_OPCION_LABEL }) {
  const [abierto, setAbierto] = useState(false);
  const [foco, setFoco] = useState(0);
  const boton = useRef(null);
  const lista = useRef(null);
  const id = useId();
  const m = ESTADO_META[estado] || { bg: 'var(--panel-hi)', border: 'var(--mesh)', text: 'var(--glass)', dot: 'var(--steel)', label: estado };
  const otras = opciones.filter(o => o !== estado);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (!lista.current?.contains(e.target) && !boton.current?.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);
  useEffect(() => { if (abierto) lista.current?.children[foco]?.focus(); }, [abierto, foco]);

  const elegir = (o) => { setAbierto(false); boton.current?.focus(); onElegir(o); };
  const teclaBoton = (e) => {
    if (['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); setFoco(0); setAbierto(true); }
  };
  const teclaLista = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAbierto(false); boton.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setFoco(f => Math.min(otras.length - 1, f + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFoco(f => Math.max(0, f - 1)); }
    else if (e.key === 'Tab') setAbierto(false);
  };
  if (!otras.length) return <EstadoBadge estado={estado} />;
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button ref={boton} type="button" aria-haspopup="menu" aria-expanded={abierto} aria-controls={id}
        onClick={() => { setFoco(0); setAbierto(a => !a); }} onKeyDown={teclaBoton}
        title="Cambiar estado"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 6px 3px 8px', borderRadius: 3,
          fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', cursor: 'pointer',
          background: m.bg, color: m.text, border: `1px solid ${m.border}`,
        }}>
        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
        {m.label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {abierto && (
        <div ref={lista} id={id} role="menu" aria-label="Cambiar estado a" onKeyDown={teclaLista}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, minWidth: 250, whiteSpace: 'nowrap',
            background: 'var(--panel)', border: '1px solid var(--mesh)', borderRadius: 4, padding: 4,
            boxShadow: '0 12px 32px rgba(17,17,17,.14)',
          }}>
          {otras.map(o => {
            const mo = ESTADO_META[o] || {};
            return (
              <button key={o} type="button" role="menuitem" tabIndex={-1} className="db-option" onClick={() => elegir(o)}
                style={{ width: '100%', border: 0, background: 'transparent', borderRadius: 3, textAlign: 'left' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: mo.dot || 'var(--steel)', flexShrink: 0 }} />
                <span>Pasar a <b style={{ color: 'var(--ink)' }}>{etiquetas[o] || o}</b></span>
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
